import React, { useState, useEffect } from 'react';
import { Button, Table, Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Pagination from './Pagination';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import InfoToggle from './InfoToggle';
import SortableHeader from '../ui/SortableHeader';
import { useSorting } from '../../hooks/useSorting';
import { getDisabledRowClass } from '../../utils/disabledUtils';
import TotalCount from './TotalCount';

const GenericList = ({
  title,
  data = [],
  columns = [],
  loading = false,
  totalCount = 0,
  currentPage = 1,
  itemsPerPage = 50,
  onPageChange,
  onItemsPerPageChange,
  onDelete,
  onEdit,
  onToggleStatus,
  createRoute,
  createButtonText = "Nuevo",
  deleteMessage = "¿Estás seguro de que deseas eliminar este elemento?",
  SearchComponent,
  searchProps = {},
  showSearch = false,
  actions = [],
  emptyMessage = "No se encontraron elementos.",
  containerFluid = true,
  responsive = "sm",
  onSort,
  sortable = false,
  renderExpandedRow
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [openSearchForm, setOpenSearchForm] = useState(true);
  const { handleSort, getSortIcon } = useSorting();
  const navigate = useNavigate();

  const handleDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete && onDelete) {
      await onDelete(itemToDelete);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleEdit = (item) => {
    if (onEdit) {
      onEdit(item);
    }
  };

  const handleCreate = () => {
    if (createRoute) {
      navigate(createRoute);
    }
  };

  const renderCellContent = (item, column) => {
    if (column.render) {
      return column.render(item);
    }
    
    const value = column.key.split('.').reduce((obj, key) => obj?.[key], item);
    
    if (column.type === 'currency') {
      return `${value}€`;
    }
    if (column.type === 'percentage') {
      return `${value?.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
    }
    if (column.type === 'boolean') {
      return value ? 'Sí' : 'No';
    }
    if (column.type === 'color') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ 
            width: '25px', 
            height: '25px', 
            backgroundColor: value, 
            border: '1px solid #000', 
            borderRadius: '50%' 
          }}></div>
        </div>
      );
    }
    
    return value || '';
  };

  const renderActions = (item) => {
    const defaultActions = [];
    
    if (onEdit) {
      defaultActions.push(
        <Button
          key="edit"
          variant="warning"
          size="sm"
          className="me-2"
          onClick={() => handleEdit(item)}
          data-testid={`edit-button-${item.id}`}
        >
          Editar
        </Button>
      );
    }
    
    if (onToggleStatus) {
      defaultActions.push(
        <Button
          key="toggle"
          variant={item.disabled ? "success" : "secondary"}
          size="sm"
          className="me-2"
          onClick={() => onToggleStatus(item.id, item.disabled)}
          data-testid={`toggle-button-${item.id}`}
        >
          {item.disabled ? "Habilitar" : "Deshabilitar"}
        </Button>
      );
    }
    
    if (onDelete) {
      defaultActions.push(
        <Button
          key="delete"
          variant="danger"
          size="sm"
          onClick={() => handleDelete(item)}
          data-testid={`delete-button-${item.id}`}
        >
          Borrar
        </Button>
      );
    }

    const customActions = actions.map((action, index) => (
      <Button
        key={`custom-${index}`}
        variant={action.variant || 'primary'}
        size="sm"
        className={action.className || 'me-2'}
        onClick={() => action.onClick(item)}
      >
        {action.label}
      </Button>
    ));

    return [...customActions, ...defaultActions];
  };

  return (
    <Container fluid={containerFluid} className="my-4">
      <Row className="mb-4">
        <Col xs={12} md={6}>
          <h1>{title}</h1>
        </Col>
        {createRoute && (
          <Col xs={12} md={6} className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleCreate} data-testid="create-button">
              {createButtonText}
            </Button>
          </Col>
        )}
      </Row>

      {showSearch && SearchComponent && (
        <Row className="mb-4">
          <Col xs={12}>
            <InfoToggle
              label="Buscar"
              openState={openSearchForm}
              handleToggle={() => setOpenSearchForm(!openSearchForm)}
              showChevron={true}
              color="#007bff"
              data-testid="search-toggle"
            />
            {openSearchForm && (
              <div className="mt-3">
                <SearchComponent {...searchProps} />
              </div>
            )}
          </Col>
        </Row>
      )}

      {loading ? (
        <p>Cargando...</p>
      ) : (
        <>
          <div className="d-flex justify-content-end mb-2">
            <TotalCount totalCount={totalCount} />
          </div>
          <div style={{ overflowX: 'auto', width: '99%' }}>
          <Table striped bordered hover responsive={responsive} style={{ minWidth: '100%' }}>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  sortable && column.sortable !== false ? (
                    <SortableHeader 
                      key={index} 
                      column={column.sortKey || column.key} 
                      onSort={onSort || handleSort} 
                      getSortIcon={getSortIcon}
                      className={column.headerClassName}
                    >
                      {column.header}
                    </SortableHeader>
                  ) : (
                    <th key={index} className={column.headerClassName}>
                      {column.header}
                    </th>
                  )
                ))}
                {(onEdit || onDelete || onToggleStatus || actions.length > 0) && <th>Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <React.Fragment key={item.id || index}>
                    <tr className={getDisabledRowClass(item.disabled)}>
                      {columns.map((column, colIndex) => (
                        <td key={colIndex} className={column.cellClassName}>
                          {renderCellContent(item, column)}
                        </td>
                      ))}
                      {(onEdit || onDelete || onToggleStatus || actions.length > 0) && (
                        <td className="text-nowrap">
                          {renderActions(item)}
                        </td>
                      )}
                    </tr>
                    {renderExpandedRow && renderExpandedRow(item)}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + (onEdit || onDelete || onToggleStatus || actions.length > 0 ? 1 : 0)} className="text-center">
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
        </>
      )}

      {totalCount > 0 && (
        <Pagination
          items={data}
          totalCount={totalCount}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
          onItemsPerPageChange={onItemsPerPageChange}
        />
      )}

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={cancelDelete}
        onConfirm={confirmDelete}
        message={typeof deleteMessage === 'function' ? deleteMessage(itemToDelete) : deleteMessage}
      />
    </Container>
  );
};

export default GenericList;